/**
 * useVinDecoder - Hook for VIN input with NHTSA decoding
 *
 * Features:
 * - VIN format validation (17 alphanumeric, no I/O/Q)
 * - Auto-decode on blur via NHTSA API
 * - Returns vehicle info (year, make, model)
 * - Error handling for invalid VINs
 */

import { useState, useCallback } from 'react';
import { decodeVin, isValidVinFormat, VehicleInfo } from '@/lib/api/nhtsaVinDecoder';

export interface UseVinDecoderReturn {
  vin: string;
  setVin: React.Dispatch<React.SetStateAction<string>>;
  vehicleInfo: VehicleInfo | null;
  isDecoding: boolean;
  error: string | null;
  handleChange: (value: string) => void;
  handleBlur: () => Promise<void>;
  reset: () => void;
}

export function useVinDecoder(): UseVinDecoderReturn {
  const [vin, setVin] = useState('');
  const [vehicleInfo, setVehicleInfo] = useState<VehicleInfo | null>(null);
  const [isDecoding, setIsDecoding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = useCallback((value: string) => {
    setVin(value.toUpperCase());
    setError(null);
  }, []);

  const handleBlur = useCallback(async () => {
    const trimmed = vin.trim().toUpperCase();
    setVehicleInfo(null);
    setError(null);

    if (!trimmed) return;

    if (!isValidVinFormat(trimmed)) {
      setError('VIN must be 17 alphanumeric characters (no I, O, Q)');
      return;
    }

    setIsDecoding(true);
    const info = await decodeVin(trimmed);
    setIsDecoding(false);

    if (!info.errorCode) {
      setVehicleInfo(info);
      setVin(trimmed);
    } else {
      setError(info.errorMessage || 'Unable to decode VIN');
    }
  }, [vin]);

  const reset = useCallback(() => {
    setVin('');
    setVehicleInfo(null);
    setError(null);
  }, []);

  return { vin, setVin, vehicleInfo, isDecoding, error, handleChange, handleBlur, reset };
}

export type { VehicleInfo };
